import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, effect, inject, signal } from "@angular/core";

import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable, filter, forkJoin, switchMap, tap } from "rxjs";

import { BackendSettings, GameFeatures, InitSettings, OperationType, Round } from "@models";
import {
  ActionService,
  AnalyticsService,
  AudioService,
  ClientService,
  CurrencyService,
  EMusic,
  EventCategory,
  GameService,
  SessionService,
  WebSocketService,
} from "@services";
import { Calculator, Utils } from "@utils";

import { BetPanelComponent } from "./components/bet-panel/bet-panel.component";
import { GamePanelComponent } from "./components/game-panel/game-panel.component";
import { HeaderComponent } from "./components/header/header.component";
import { HowToPlayModalComponent } from "./components/how-to-play-modal/how-to-play-modal.component";
import { MenuComponent } from "./components/menu/menu.component";
import { ModalService } from "./components/modal/modal.service";
import { SplashScreenComponent } from "./components/splash-screen/splash-screen.component";

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    landscape: boolean;
    AudioContext: any;
    webkitAudioContext: any;
  }
}

enum QueryParams {
  T = "token",
}

@UntilDestroy()
@Component({
  selector: "app-root",
  styleUrls: ["./app.component.scss"],
  templateUrl: "./app.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HeaderComponent, SplashScreenComponent, BetPanelComponent, GamePanelComponent, MenuComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  protected readonly acS = inject(ActionService);
  private readonly seS = inject(SessionService);
  private readonly gS = inject(GameService);
  private readonly anS = inject(AnalyticsService);
  private readonly crS = inject(CurrencyService);
  private readonly mS = inject(ModalService);
  private readonly auS = inject(AudioService);
  private readonly clS = inject(ClientService);
  private readonly wS = inject(WebSocketService);

  public loading = signal<boolean>(true);

  private interval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    effect(() => {
      if (this.clS.stop$()) {
        this.mS.closeAll();
      }
    });
  }

  public ngOnInit(): void {
    this.anS.init();
    this.mS.init();

    this.seS
      .create(new URLSearchParams(window.location.search).get(QueryParams.T) || "")
      .pipe(
        tap(resp => {
          this.anS.track("session_created", EventCategory.S);
          this.crS.initCurrency(resp);
          this.clS.init(resp.game_id);
          this.acS.sR$.set(resp);
          this.acS.ba$.set(resp.balance);
          this.acS.cS$.set(Utils.seed());
          this.wS.init();
        }),
        switchMap(() => forkJoin([this.sD(), this.uD()])),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.auS.init();
        this.auS.music(EMusic.B);
        this.loading.set(false);
        this.tutorial();
        this.health();
      });

    this.acS.oE$
      .pipe(
        tap(event => {
          switch (event.type) {
            case OperationType.P:
              this.acS.ba$.update(balance => this.round(Calculator.sum(balance, event.value)));
              break;
            case OperationType.M:
              this.acS.ba$.update(balance => {
                const result = Calculator.min(balance, event.value);
                return Calculator.lt(result, "0") ? "0" : this.round(result);
              });
              break;
            default:
              break;
          }
        }),
        filter(event => event.type === OperationType.U),
        switchMap(() => this.balance()),
        untilDestroyed(this),
      )
      .subscribe();

    this.clS.ref$
      .pipe(
        switchMap(() => this.uD()),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private sD(): Observable<[BackendSettings, InitSettings]> {
    return forkJoin([this.seS.settings(), this.gS.init()]).pipe(
      tap(([boSettings, initSettings]) => {
        this.acS.cSB$.set(boSettings.currency);
        this.acS.gSB$.set(boSettings.game);
        this.acS.iS$.set(initSettings);

        const active = initSettings.active_round;
        if (active && !active.finished) {
          this.acS.aR$.set(active);
          this.clS.set("mines_count", active.data.mines_count);
        }
      }),
    );
  }

  private uD(): Observable<[string, Round[]]> {
    return forkJoin([this.balance(), this.hD()]);
  }

  private balance(): Observable<string> {
    return this.seS.balance().pipe(tap(balance => this.acS.ba$.set(this.round(balance || "0"))));
  }

  private hD(): Observable<Round[]> {
    return this.gS.history().pipe(tap(rounds => this.acS.rd$.set(rounds)));
  }

  public ngOnDestroy(): void {
    this.clear();
    this.wS.destroy();
    this.mS.destroy();
    this.clS.destroy();
    this.auS.destroy();
  }

  private health(): void {
    this.clear();

    this.interval = setInterval(() => {
      this.seS.status().pipe(untilDestroyed(this)).subscribe();
    }, Utils.second * 180);
  }

  private tutorial(): void {
    if (this.acS.gSB$()?.enabled_features?.includes(GameFeatures.T) && !this.clS.sF$()?.skipTutorial) {
      this.mS.open({ component: HowToPlayModalComponent, outside: true, styleClass: "m-h-t-p" });
    }
  }

  private round(value: string): string {
    return this.crS.round({ value });
  }

  private clear(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
}
